/**
 * useVoiceSession — React hook for Pipecat voice pipeline integration.
 *
 * Connects to the voice service via WebSocket, handles audio I/O,
 * and exposes voice state + transcription callbacks compatible with
 * the existing VoiceMicButton and MyAgentsTab patterns.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { PipecatClient } from '@pipecat-ai/client-js';
import type { TransportState } from '@pipecat-ai/client-js';
import { WebSocketTransport, WavMediaManager } from '@pipecat-ai/websocket-transport';
import { useAuthStore } from '@/stores/auth/auth-store';
import type { VoiceState } from '@/services/voice/types';
import { agentVoiceApi } from '@/services/api/agentApi';

const VOICE_SERVICE_URL = import.meta.env.VITE_VOICE_SERVICE_URL || 'ws://localhost:8007';

interface UseVoiceSessionReturn {
  voiceState: VoiceState;
  error: string | null;
  isConnected: boolean;
  client: PipecatClient | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  setOnTranscription: (cb: (text: string) => void) => void;
  setOnBotResponse: (cb: (text: string) => void) => void;
}

/**
 * Map Pipecat transport states + RTVI events to our VoiceState enum.
 */
function mapTransportState(state: TransportState, isBotSpeaking: boolean, isUserSpeaking: boolean): VoiceState {
  switch (state) {
    case 'disconnected':
      return 'idle';
    case 'initializing':
    case 'initialized':
    case 'authenticating':
    case 'authenticated':
    case 'connecting':
      return 'loading-models';
    case 'connected':
    case 'ready':
      if (isBotSpeaking) return 'speaking';
      if (isUserSpeaking) return 'speech-detected';
      return 'listening';
    case 'disconnecting':
      return 'idle';
    case 'error':
      return 'error';
    default:
      return 'idle';
  }
}

export function useVoiceSession(): UseVoiceSessionReturn {
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [client, setClient] = useState<PipecatClient | null>(null);

  const clientRef = useRef<PipecatClient | null>(null);
  const onTranscriptionRef = useRef<((text: string) => void) | null>(null);
  const onBotResponseRef = useRef<((text: string) => void) | null>(null);
  const isBotSpeakingRef = useRef(false);
  const isUserSpeakingRef = useRef(false);
  const connectingRef = useRef(false);

  const accessToken = useAuthStore((s) => s.tokens?.accessToken);

  const updateVoiceState = useCallback((transportState: TransportState) => {
    const state = mapTransportState(transportState, isBotSpeakingRef.current, isUserSpeakingRef.current);
    setVoiceState(state);
  }, []);

  const connect = useCallback(async () => {
    if (connectingRef.current || clientRef.current) return;
    connectingRef.current = true;
    setError(null);

    try {
      const transport = new WebSocketTransport({
        mediaManager: new WavMediaManager(4096, 16000),
        playerSampleRate: 24000,
        recorderSampleRate: 16000,
      });

      const client = new PipecatClient({
        transport,
        enableMic: true,
        callbacks: {
          onTransportStateChanged: (state: TransportState) => {
            console.log('[useVoiceSession] Transport state:', state);
            updateVoiceState(state);
            const connected = state === 'ready' || state === 'connected';
            setIsConnected(connected);
          },
          onBotReady: () => {
            console.log('[useVoiceSession] Bot ready');
            setVoiceState('listening');
            setIsConnected(true);
          },
          onTrackStarted: (track: MediaStreamTrack, participant: any) => {
            console.log('[useVoiceSession] Track started:', track.kind, track.id, 'participant:', participant);
          },
          onTrackStopped: (track: MediaStreamTrack, participant: any) => {
            console.log('[useVoiceSession] Track stopped:', track.kind, track.id);
          },
          onDisconnected: () => {
            setVoiceState('idle');
            setIsConnected(false);
            isBotSpeakingRef.current = false;
            isUserSpeakingRef.current = false;
            clientRef.current = null;
            setClient(null);
          },
          onUserStartedSpeaking: () => {
            isUserSpeakingRef.current = true;
            setVoiceState('speech-detected');
          },
          onUserStoppedSpeaking: () => {
            isUserSpeakingRef.current = false;
            setVoiceState('transcribing');
          },
          onBotStartedSpeaking: () => {
            console.log('[useVoiceSession] Bot STARTED speaking');
            isBotSpeakingRef.current = true;
            setVoiceState('speaking');
          },
          onBotStoppedSpeaking: () => {
            console.log('[useVoiceSession] Bot STOPPED speaking');
            isBotSpeakingRef.current = false;
            setVoiceState('listening');
          },
          onUserTranscript: (data: any) => {
            console.log('[useVoiceSession] User transcript:', data);
            if (data?.final && data?.text?.trim()) {
              onTranscriptionRef.current?.(data.text);
            }
          },
          onBotTranscript: (text: any) => {
            console.log('[useVoiceSession] Bot transcript:', text);
            onBotResponseRef.current?.(typeof text === 'string' ? text : text?.text);
          },
          onBotLlmText: (data: any) => {
            console.log('[useVoiceSession] Bot LLM text:', data);
          },
          onBotLlmStarted: () => {
            console.log('[useVoiceSession] Bot LLM started');
          },
          onBotLlmStopped: () => {
            console.log('[useVoiceSession] Bot LLM stopped');
          },
          onBotTtsStarted: () => {
            console.log('[useVoiceSession] Bot TTS started');
          },
          onBotTtsStopped: () => {
            console.log('[useVoiceSession] Bot TTS stopped');
          },
          onError: (err: Error) => {
            console.error('[useVoiceSession] Error:', err);
            setError(err.message);
            setTimeout(() => setError(null), 5000);
          },
        },
      });

      clientRef.current = client;
      setClient(client);

      // Fetch personal agent ID for per-agent voice blend
      let agentParam = '';
      try {
        const agentRes = await agentVoiceApi.personalAgent();
        console.log('[useVoiceSession] personalAgent response:', agentRes);
        if (agentRes.success && agentRes.data?._id && agentRes.data.voice_config?.blend?.length) {
          agentParam = `&agent_id=${encodeURIComponent(agentRes.data._id)}`;
          console.log('[useVoiceSession] Using agent_id:', agentRes.data._id);
        }
      } catch (err) {
        console.warn('[useVoiceSession] Failed to fetch personal agent ID:', err);
      }

      // Build the WebSocket URL with JWT token
      const wsUrl = `${VOICE_SERVICE_URL}/ws?token=${encodeURIComponent(accessToken || '')}${agentParam}`;

      await client.connect({ wsUrl });
    } catch (err) {
      console.error('[useVoiceSession] Connect failed:', err);
      setError(err instanceof Error ? err.message : String(err));
      setVoiceState('error');
      clientRef.current = null;
      setClient(null);
      setTimeout(() => setError(null), 5000);
    } finally {
      connectingRef.current = false;
    }
  }, [accessToken, updateVoiceState]);

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.disconnect();
      clientRef.current = null;
    }
    setClient(null);
    isBotSpeakingRef.current = false;
    isUserSpeakingRef.current = false;
    setIsConnected(false);
    setVoiceState('idle');
  }, []);

  const setOnTranscription = useCallback((cb: (text: string) => void) => {
    onTranscriptionRef.current = cb;
  }, []);

  const setOnBotResponse = useCallback((cb: (text: string) => void) => {
    onBotResponseRef.current = cb;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
    };
  }, []);

  return {
    voiceState,
    error,
    isConnected,
    client,
    connect,
    disconnect,
    setOnTranscription,
    setOnBotResponse,
  };
}
